// src/components/customers/CreateCustomer.jsx
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const CreateCustomer = ({ onCustomerCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Memoized event handlers
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Customer name is required', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post(API_CONFIG.CUSTOMERS.CREATE_CUSTOMER, {
        ...formData,
        name: formData.name.trim()
      });

      const responseData = response.data;
      
      if (responseData.success) {
        showToast('Customer created successfully', 'success');
        setFormData({ name: '', email: '', phone: '', company: '' });
        onCustomerCreated?.();
      } else {
        showToast(responseData.message || 'Failed to create customer', 'error');
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [formData, onCustomerCreated]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleBackClick = useCallback(() => {
    navigate('/customers');
  }, [navigate]);

  // Quick action cards data
  const quickActions = [
    {
      icon: (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      ),
      title: 'Required Fields',
      description: 'Only name is required',
      bgColor: 'bg-blue-100'
    },
    {
      icon: (
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
        </svg>
      ),
      title: 'Data Privacy',
      description: 'Your customer data is secure',
      bgColor: 'bg-green-100'
    }
  ];

  // Tips data
  const customerTips = [
    'Collecting customer information helps personalize their experience',
    'Phone numbers are essential for transaction notifications',
    'Email addresses allow for receipt delivery',
    'Company names help organize business customers'
  ];

  return (
    <div className="min-h-full">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-gray-900 truncate" style={{ fontSize: '18px' }}>
                Add New Customer
              </h1>
              <p className="text-gray-600 mt-1 truncate" style={{ fontSize: '14px' }}>
                Enter customer details to add to your records
              </p>
            </div>
            <button
              onClick={handleBackClick}
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium whitespace-nowrap"
              style={{ fontSize: '14px' }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Back to Customers
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Customer Form - Takes 2/3 on desktop */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900" style={{ fontSize: '16px' }}>
                    Customer Information
                  </h2>
                  <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                    Fill in the customer details below
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="customerName"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter customer's full name"
                      required
                      style={{ fontSize: '14px' }}
                      maxLength={100}
                    />
                    <div className="text-right mt-1">
                      <span className="text-gray-500" style={{ fontSize: '12px' }}>
                        {formData.name.length}/100 characters
                      </span>
                    </div>
                  </div>
                  
                  {/* Email Address */}
                  <div>
                    <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="customerEmail"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="customer@example.com"
                      style={{ fontSize: '14px' }}
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Phone Number
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        id="customerPhone"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+234 800 000 0000"
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                  </div>
                  
                  {/* Company Name */}
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      id="companyName"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company name (optional)"
                      style={{ fontSize: '14px' }}
                      maxLength={100}
                    />
                    <div className="text-right mt-1">
                      <span className="text-gray-500" style={{ fontSize: '12px' }}>
                        {formData.company.length}/100 characters
                      </span>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button 
                    type="submit" 
                    disabled={isLoading || !formData.name.trim()}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    style={{ fontSize: '15px' }}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding Customer...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
                        </svg>
                        Add Customer
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Tips and Quick Actions - Takes 1/3 on desktop */}
          <div className="space-y-6">
            {/* Tips Section */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
              <div className="flex items-start mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-blue-600 text-sm">💡</span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-blue-800 mb-2" style={{ fontSize: '15px' }}>
                    Customer Management Tips
                  </h3>
                  <ul className="text-blue-700 space-y-2" style={{ fontSize: '14px' }}>
                    {customerTips.map((tip, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
              {quickActions.map((action, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${action.bgColor}`}>
                      {action.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-700" style={{ fontSize: '14px' }}>{action.title}</p>
                      <p className="text-gray-500 mt-1" style={{ fontSize: '13px' }}>{action.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Info Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>Why Collect Customer Data?</h4>
                  <p className="text-gray-600" style={{ fontSize: '13px' }}>
                    Building a customer database helps provide better service, personalized experiences, and improves customer retention.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomer;